<template>
  <div class="w-full h-full px-4 overflow-y-auto sm:px-6 md:px-8">
    <div class="flex justify-between w-full min-w-0 py-2 mt-3">
      <h1 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
        {{ $t("Admin") }}
      </h1>
      <nav class="flex justify-between w-full">
        <ol class="flex items-center flex-1 ml-3">
          <li>
            <div class="flex items-center">
              <ChevronRight class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" />
              <router-link to="/admin/integrations" class="'ml-2 text-sm font-medium capitalize text-gray-500 hover:text-gray-700">
                {{ $t("Integrations") }}
              </router-link>
            </div>
          </li>
        </ol>
      </nav>
    </div>
    <!-- Pinned services -->
    <div v-if="is_page_ready" class="h-full mt-8">
      <!-- Projects table (small breakpoint and up) -->
      <div v-if="available_services?.length > 0 && !(current_file || sheet_link_ready) && !show_google_drive_sheets">
        <div class="flex flex-col flex-wrap min-w-full gap-2 mb-0 overflow-y-visible align-middle justify-items-start">
          <!-- search bar -->
          <div class="flex gap-2">
            <input
              v-if="available_services?.length > 0"
              v-model="search"
              type="text"
              class="w-10/12 h-10 px-4 mb-4 text-sm text-gray-700 placeholder-gray-400 bg-white border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-light-brand-blue focus:border-light-brand-blue dark:bg-dark-screen-background dark:border-dark-divider-border"
              :placeholder="$t('Search')">
            <SelectInput
              v-model="selected_category"
              name="category"
              :items="
                Object.keys($utils.groupBy(available_services, 'category')).map((x) => ({
                  id: x,
                  name: $utils.capitalize(x).replace('_', ' ')
                }))
              "
              label-attr="name"
              value-attr="id"
              classes="w-2/12"
              :placeholder="$t('Category')"
              :label="$t('Category')" />
          </div>
          <div
            v-for="[type, services] in Object.entries(
              $utils.groupBy(
                available_services
                  .filter((x) => x.category == selected_category || !selected_category)
                  .filter((x) => x.title?.includes(search) || x.name?.includes(search) || !search),
                'type'
              )
            )"
            :key="type"
            class="flex flex-col gap-2">
            <span class="w-full text-lg font-semibold dark:text-dark-body-text">{{
              type == "active"
                ? $t("Connected Integrations")
                : type.toLowerCase() == "data"
                  ? $t("Data Sources")
                  : $t("Collaborations tools")
            }}</span>

            <div class="flex space-x-2">
              <div
                v-for="(sub_category, idx) in getSubCategories(available_services.filter((s) => s.type == type))"
                :key="idx"
                :class="[{ 'text-white !bg-light-brand-blue': selected_category == sub_category }]"
                class="bg-white border rounded-full p-2 px-4 mr-2 cursor-pointer flex items-center"
                @click="selected_category == sub_category ? (selected_category = null) : (selected_category = sub_category)">
                {{ sub_category.replace("_", " ") }} <X v-if="selected_category == sub_category" color="white" class="w-5 h-5 ml-2" />
              </div>
            </div>
            <div class="flex flex-row flex-wrap min-w-full gap-2 mb-0 overflow-visible align-middle justify-items-start">
              <IntegrationCard
                v-for="service in $utils
                  .orderBy(services, ['status', 'integration_provider', 'category', 'name'], ['desc', 'desc', 'desc', 'desc'])
                  .reverse()"
                :key="service.id"
                v-model:all-services="available_services"
                :service="service"
                @service-deleted="handleServiceDeleted" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <router-view v-slot="{ Component }">
      <keep-alive>
        <component :is="Component" :key="$route.fullPath" />
      </keep-alive>
    </router-view>
  </div>
</template>

<script>
import IntegrationService from "@/services/integration.service"
import { ChevronRight, X } from "lucide-vue-next"
import IntegrationCard from "@/views/dashboard/admin/integrations/IntegrationCard.vue"
import SelectInput from "@/components/shared/SelectInput.vue"

export default {
  name: "IntegrationsList",
  components: {
    IntegrationCard,
    ChevronRight,
    SelectInput,
    X
  },

  data() {
    return {
      search: "",
      available_services: [],
      show_google_drive_sheets: false,
      sheet_link_ready: false,
      selected_category: null,
      current_file: "",
      is_page_ready: false
    }
  },

  created() {
    this.getIntegratedServices()
  },
  methods: {
    getSubCategories(services) {
      const list_calc = []
      services.forEach((s) => {
        if (!list_calc.includes(s.category)) {
          list_calc.push(s.category)
        }
      })
      return list_calc
    },
    getIntegratedServices() {
      this.showLoading()
      this.is_page_ready = false
      IntegrationService.allServices()
        .then(({ data }) => {
          this.available_services = data.map((s) => {
            return { ...s, type: s.status == "on" ? "active" : s.type }
          })
          // const csv_integration = {
          //   id: "csv_integration",
          //   reference: "csv-upload",
          //   service_provider: "Revsetter",
          //   name: "csv-upload",
          //   title: "Upload File",
          //   description: "Upload CSV, XSLS or Numbers file",
          //   logo_url: "https://cdn-icons-png.flaticon.com/512/8242/8242984.png",
          //   type: "data",
          //   category: "file_upload",
          //   status: "off"
          // }
          const google_sheet = {
            id: "google_sheet",
            reference: "google-sheet",
            service_provider: "Revsetter",
            name: "google-sheet",
            title: "Google Sheet",
            description: "Enter google sheet URL",
            logo_url: "https://storage.googleapis.com/revsetter-public/integrations-logos/spreadsheet.png",
            type: "data",
            category: "file_upload",
            status: "off"
          }
          if (this.$store.state.integration.integrations.find((i) => i.reference == "google-drive")) {
            this.available_services.push(google_sheet)
          }
          // if (!(this.current_file || this.sheet_link_ready)) {
          //   this.available_services.push(csv_integration)
          // }
          this.hideLoading()
          this.is_page_ready = true
          this.refreshServicesRealtimeUpdatesStatus()
        })
        .catch(() => {
          this.hideLoading()
        })
    },
    showLoading(message = "") {
      this.$store.dispatch("ui/showLoading", message)
    },
    hideLoading() {
      this.$store.dispatch("ui/hideLoading")
    },
    handleServiceDeleted(serviceId) {
      // Remove the deleted service from available_services
      this.available_services = this.available_services.filter((s) => s.id !== serviceId)

      // Force a refresh of the component
      this.$forceUpdate()

      // Optionally, you can also refresh the data from the server
      this.getIntegratedServices()
    },
    refreshServicesRealtimeUpdatesStatus() {
      this.available_services?.forEach((service) => {
        if (service.reference == "salesforce" && service.integration_provider == "revsetter" && service.status == "on") {
          this.getServiceRealtimeUpdatesStatus(service)
        }
      })
    },
    getServiceRealtimeUpdatesStatus(service) {
      if (service.status == "on") {
        const service_name = service.name
        this.$data["services_realtime_updates_status"][service_name] = {
          checking_for_status: true
        }

        IntegrationService.getServiceRealtimeUpdatesStatus(service_name).then((res) => {
          this.$data["services_realtime_updates_status"][service_name] = {
            ...res.data,
            checking_for_status: false
          }
        })
      }
    }
  }
}
</script>

<style>
.custom-file-input {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  border: none;
  background-color: transparent;
  resize: none;
  outline: none;
  cursor: pointer;
}
</style>
